import * as React from 'react';
import { Transition as BaseTransition } from 'react-transition-group';

import useForkRef from '../hooks/useForkRef';
import { reflow } from '../utils';
import { TransitionProps, TransitionStates } from './types';
import { animationClassesGenerator } from './utils';

const Transition = (props: TransitionProps, ref: any) => {
  const {
    appear,
    children,
    classes,
    in: inProp,
    onEnter,
    onEntered,
    onEntering,
    onExit,
    onExited,
    onExiting,
    style,
    TransitionComponent = BaseTransition,
    ...other
  } = props;

  const nodeRef = React.useRef(null);
  // @ts-ignore
  const foreignRef = useForkRef(children.ref, ref);
  const handleRef = useForkRef(nodeRef, foreignRef);

  const getNodeAnimationClasses = React.useMemo(() => {
    return animationClassesGenerator(classes);
  }, [classes]);

  const normalizedTransitionCallback = (callback: any) => (maybeIsAppearing: boolean) => {
    if (callback) {
      const node = nodeRef.current;

      if (maybeIsAppearing === undefined) {
        callback(node);
      } else {
        callback(node, maybeIsAppearing);
      }
    }
  };

  const handleEnter = normalizedTransitionCallback((node: HTMLElement, isAppearing: boolean) => {
    reflow(node); // So the animation always start from the start.
    const { classesToAdd } = getNodeAnimationClasses('enter');
    node.classList.add(...classesToAdd);

    if (onEnter) {
      onEnter(node, isAppearing);
    }
  });

  const handleEntering = normalizedTransitionCallback((node: HTMLElement, isAppearing: boolean) => {
    const { classesToAdd, classesToRemove } = getNodeAnimationClasses('entering');
    node.classList.remove(...classesToRemove);
    node.classList.add(...classesToAdd);

    if (onEntering) {
      onEntering(node, isAppearing);
    }
  });

  const handleEntered = normalizedTransitionCallback((node: HTMLElement, isAppearing: boolean) => {
    const { classesToRemove } = getNodeAnimationClasses('entered');
    node.classList.remove(...classesToRemove);

    if (onEntered) {
      onEntered(node, isAppearing);
    }
  });

  const handleExit = normalizedTransitionCallback((node: HTMLElement) => {
    const { classesToAdd } = getNodeAnimationClasses('exit');
    node.classList.add(...classesToAdd);

    if (onExit) {
      onExit(node);
    }
  });

  const handleExiting = normalizedTransitionCallback((node: HTMLElement) => {
    const { classesToAdd, classesToRemove } = getNodeAnimationClasses('exiting');
    node.classList.remove(...classesToRemove);
    node.classList.add(...classesToAdd);

    if (onExiting) {
      onExiting(node);
    }
  });

  const handleExited = normalizedTransitionCallback((node: HTMLElement) => {
    const { classesToRemove } = getNodeAnimationClasses('entering');
    node.classList.remove(...classesToRemove);

    if (onExited) {
      onExited(node);
    }
  });

  return (
    <TransitionComponent
      appear={appear}
      in={inProp}
      nodeRef={nodeRef}
      onEnter={handleEnter}
      onEntered={handleEntered}
      onEntering={handleEntering}
      onExit={handleExit}
      onExited={handleExited}
      onExiting={handleExiting}
      {...other}>
      {(state: TransitionStates, childProps: any) => {
        return React.cloneElement(children, {
          ref: handleRef,
          style: {
            visibility: state === 'exited' && !inProp ? 'hidden' : undefined,
            ...style,
            ...children.props.style,
          },
          ...childProps,
        });
      }}
    </TransitionComponent>
  );
};

const RefForwardedTransition = React.forwardRef<any, TransitionProps>(Transition);

export default RefForwardedTransition as typeof Transition;
